import { toast } from "react-toastify";
import {
  openWaitingRoomTab,
  saveWaitingRoomSession,
} from "./videoSessionDevices";

export const THERAPIST_JOIN_WINDOW_MINUTES_BEFORE = 15;
export const THERAPIST_JOIN_MVP_ALWAYS_SHOW = true;

const CANCELLED_STATUSES = new Set([
  "Cancelled",
  "Canceled",
  "canceled_by_user",
  "Canceled by Provider",
  "Canceled by Super Admin",
  "System Cancel",
  "late_cancelled",
  "late_cancel",
]);

const getAppointmentEndTime = (appointment) => {
  if (!appointment?.appointmenttime) return null;

  if (appointment.appointmentEndTime) {
    return new Date(appointment.appointmentEndTime);
  }

  const startTime = new Date(appointment.appointmenttime);
  const durationMinutes = appointment.sessionLength || 30;

  return new Date(startTime.getTime() + durationMinutes * 60000);
};

export const isCancelledTherapistSession = (appointment) =>
  Boolean(
    appointment?.isCancelled ||
      CANCELLED_STATUSES.has(String(appointment?.status || "").trim()),
  );

export const isWithinTherapistJoinWindow = (appointment) => {
  if (!appointment?.appointmenttime) return false;

  const now = Date.now();
  const startTime = new Date(appointment.appointmenttime).getTime();
  const endTime = getAppointmentEndTime(appointment)?.getTime();

  if (!endTime || Number.isNaN(startTime) || Number.isNaN(endTime)) {
    return false;
  }

  const minutesUntilStart = (startTime - now) / (1000 * 60);
  const hasStarted = now >= startTime;
  const hasEnded = now >= endTime;

  return (
    minutesUntilStart <= THERAPIST_JOIN_WINDOW_MINUTES_BEFORE &&
    !hasEnded &&
    (hasStarted || minutesUntilStart >= 0)
  );
};

export const shouldShowTherapistJoinButton = (appointment) => {
  if (!appointment || isCancelledTherapistSession(appointment)) {
    return false;
  }

  if (appointment.status === "Completed" || appointment.status === "No Show") {
    return false;
  }

  if (THERAPIST_JOIN_MVP_ALWAYS_SHOW) {
    return true;
  }

  return isWithinTherapistJoinWindow(appointment);
};

export const buildWaitingRoomAppointment = (session) => ({
  _id: session._id || session.id,
  appointmenttime: session.appointmenttime || session.sortTime,
  appointmentEndTime: session.appointmentEndTime || null,
  sessionLength: session.sessionLength || 30,
  appointmentType: session.appointmentType || null,
  status: session.status || "Upcoming",
  isCancelled: Boolean(session.isCancelled),
  agoraAppId: session.agoraAppId || null,
  agoraAppToken: session.agoraAppToken || null,
  agoraChannelName: session.agoraChannelName || null,
  user: session.user || {
    _id: session.userId,
    firstname: session.patientFirstName,
    lastname: session.patientLastName,
  },
});

export const launchTherapistWaitingRoom = (session, navigate) => {
  const appointment = buildWaitingRoomAppointment(session);

  if (!shouldShowTherapistJoinButton(appointment)) {
    toast.error("This session is no longer available to join.");
    return false;
  }

  if (openWaitingRoomTab(appointment)) {
    return true;
  }

  if (typeof navigate === "function") {
    saveWaitingRoomSession(appointment);
    navigate("/waiting-room", { state: { appointment } });
    return true;
  }

  toast.error(
    "Unable to open the waiting room. Please allow pop-ups and try again.",
  );
  return false;
};
